/* eslint-disable no-shadow, react/sort-comp */
import classnames from 'classnames';
import React from 'react';
import Keys from 'es6/core/keys';
import Localization from 'es6/services/localization';
import FitText from 'es6/components/common/fit-text';
import { MCQuizChoice, MCQuizChoices } from 'es6/components/apps/mc-quiz-choices';
import { RAPID_CHOICE_CORRECT, RAPID_CHOICE_INCORRECT, TIMEOUT } from 'es6/core/sound-effects';
import SoundManager from 'es6/services/sound-manager';

const INITIAL_STATE = {
  answered: false,
  chosenIndex: -1,
  fadeOut: false,
  isCorrect: false,
  timeout: null,
};

const IS_CORRECT_INTERVAL = 500;

const IS_INCORRECT_INTERVAL = 1000;

const FADE_OUT_TIME = 300;

/**
 * Handles the animation phase of the rapid choice component.
 * This is essentially a two part phase.
 *
 * One is to give the user enough time to see the results of their
 * answer (which may or may not be different for correct and incorrect).
 *
 * The second part is to perform the fade out animation and send back
 * the onQuizFinished response.
 *
 * @this RapidChoice
 */
function animateAndFadeOut() {
  const { isCorrect, fadeOut } = this.state;

  // Already fading out, no need to do anything.
  if (fadeOut) {
    return;
  }

  // Animate for 500 milliseconds if the user was correct.
  // Animate for 1000 if they were wrong.
  // After the animation, perform the transition and move to the next quiz
  const timeoutInterval = isCorrect ? IS_CORRECT_INTERVAL : IS_INCORRECT_INTERVAL;
  const timeout = setTimeout(() => {
    const timeout = setTimeout(this.props.onQuizFinished.bind(this), FADE_OUT_TIME);
    this.setState({ fadeOut: true, timeout });
  }, timeoutInterval);

  this.setState({ timeout });
}

/**
 * Callback for when an option is clicked.
 *
 * @this RapidChoice
 * @param {Number} choiceIndex     the index of the choice made
 */
function handleAnswer(chosenIndex) {
  const { correctChoiceIndex, timedOut } = this.props;

  // Only allow the quiz to be completed once.
  if (this.state.answered || timedOut) {
    return;
  }

  const isCorrect = chosenIndex === correctChoiceIndex;

  if (isCorrect) {
    SoundManager.playEffect(RAPID_CHOICE_CORRECT);
  } else {
    SoundManager.playEffect(RAPID_CHOICE_INCORRECT);
  }

  this.setState({ answered: true, chosenIndex, isCorrect });
  this.props.onAnswer.call(this, isCorrect, chosenIndex);
  animateAndFadeOut.call(this);
}

/**
 * Plays the sound only if rendering a cue.
 */
function playSound() {
  const { recallPromptSound } = this.props;

  if (recallPromptSound) {
    SoundManager.play(recallPromptSound, {}, true);
  }
}

/**
 * Helper method to render a choice.
 *
 * @this RapidChoice
 * @param {Number} choiceIndex   index of choice to render
 * @param {Array} keyCode        keyCode to pass into the MCQuizChoice
 */
function renderChoice(choiceIndex, keyCode) {
  const { correctChoiceIndex, choices, timedOut } = this.props;
  const { answered, chosenIndex, isCorrect } = this.state;

  // Is the choice that was chosen?
  const isChosenIndex = choiceIndex === chosenIndex;

  const className = classnames({
    'mc-quiz-choice-correct': answered && isChosenIndex && isCorrect,
    'mc-quiz-choice-incorrect': answered && isChosenIndex && !isCorrect,
    'mc-quiz-choice-highlight': timedOut && choiceIndex === correctChoiceIndex,
  });

  const choice = choices[choiceIndex];
  const onClick = handleAnswer.bind(this, choiceIndex);

  const props = {
    key: choice,
    keyCode,
    className,
    onClick,
    disabled: this.isDone(),
  };

  return (<MCQuizChoice {...props}>{choice}</MCQuizChoice>);
}

class RapidChoice extends React.Component {
  constructor(...args) {
    super(...args);
    this.state = INITIAL_STATE;
  }

  componentWillMount() {
    playSound.call(this);
  }

  // Play the item package sound if answered or timed out are not set.
  componentDidUpdate() {
    const { timedOut } = this.props;
    const { answered } = this.state;

    if (!timedOut && !answered) {
      playSound.call(this);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.state.timeout);
  }

  /**
   * If the choices change, reset the state.
   */
  componentWillReceiveProps(nextProps) {
    const { choices, timedOut } = this.props;

    // Essentially resets the state.
    if (nextProps.choices !== choices) {
      clearTimeout(this.state.timeout);
      this.setState(INITIAL_STATE);
    }

    // If timed out flag is set, play the timed out audio and fade out.
    if (nextProps.timedOut && !timedOut) {
      SoundManager.playEffect(TIMEOUT);
      animateAndFadeOut.call(this);
    }
  }

  isDone() {
    // If the animation delay or fade out animation has started, then the quiz is over.
    return !!(this.state.timeout || this.state.fadeOut);
  }

  /**
   * Because we don't want the arrow events to work on the multiple choice menu,
   * just disable it and let the choices handle the events themselves.
   */
  render() {
    const { recallPrompt } = this.props;
    const recallPromptClasses = classnames('recall-prompt animated', {
      'fade-out-left': this.state.fadeOut,
    });

    return (
      <div className="screen screen-rapid-choice">
        <div className="screen-content">
          <div className="message">
            <p className="know">{Localization.t('do you know this word?')}</p>
          </div>

          <p className={recallPromptClasses}>
            <FitText minFontSize={20}>{recallPrompt}</FitText>
          </p>

          {/* Set a key to reset the focus when the quiz changes. */}
          <MCQuizChoices key={recallPrompt} enableArrowKeys={false} disabled={this.isDone()} className="mc-quiz-choices-center">
            {renderChoice.call(this, 0, [Keys[1], Keys.UP, Keys.K])}
            {renderChoice.call(this, 1, [Keys[2], Keys.DOWN, Keys.J])}
          </MCQuizChoices>
        </div>
      </div>
    );
  }
}

RapidChoice.propTypes = {
  correctChoiceIndex: React.PropTypes.number.isRequired,
  choices: React.PropTypes.array.isRequired,
  onAnswer: React.PropTypes.func.isRequired,
  onQuizFinished: React.PropTypes.func.isRequired,
  recallPrompt: React.PropTypes.string.isRequired,
  recallPromptSound: React.PropTypes.string,
  timedOut: React.PropTypes.bool,
};

RapidChoice.defaultProps = {
  recallPromptSound: null,
  timedOut: false,
};

export default RapidChoice;
